import React, { useState, useEffect } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";

import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";

import ReceiptTemplate from "../InvoiceComp/ReceiptTemplate";
import ReceiptTemplate_MSA from "../InvoiceComp/ReceiptTemplate_MSA";

import {
	localTimeToPickerTime,
	pickerTimeTolocalTime,
} from "../../../util/momentTimezone";

const ReceiptForm = (props) => {
	const getInitStartDate = (paymentDate) =>
		paymentDate != null ? moment(paymentDate) : moment();
	const initStartDate = getInitStartDate(
		props.selectedReceipt.receipt.paymentDate,
	);
	const initialInvoice = props.selectedReceipt.invoice;
	const isMSA = Array.isArray(initialInvoice)
		? initialInvoice[0]?.operation_status === 2
		: initialInvoice?.operation_status === 2;
	const [selectedDate, setSelectedDate] = useState(initStartDate);
	const [minDate, setMinDate] = useState(moment().subtract(1, "month"));
	const [selectedReceipt, setSelectedReceipt] = useState(props.selectedReceipt);
	const [lastUpdate, setLastUpdate] = useState(props.selectedReceipt.receipt);
	const isMSAReceipt = isMSA;
	const user = useUser();
	const username = user.name;
	const userID = user.sub;

	// Get invoice_no for PDF rendering
	const invoiceNo = Array.isArray(initialInvoice)
		? initialInvoice[0]?.invoice_no
		: initialInvoice?.invoice_no;

	const resetForm = props.resetForm;
	const lastUpdateStyle = {
		textAlign: "right",
		paddingRight: "15px",
	};

	/**
	 * Handle submit form
	 * @param {*} e event
	 */
	const handleSubmit = (e) => {
		const currentReceipt = selectedReceipt.receipt;
		const now = moment().format("YYYY-MM-DD HH:mm:ss");
		const paymentDate = moment(currentReceipt.paymentDate).format("DD/MM/YYYY");

		const tempReceipt = { ...currentReceipt, issuedAt: now };

		const confirmSubmit = window.confirm(
			`Confirm payment date: ${paymentDate}?`,
		);

		if (confirmSubmit) submitIssue(tempReceipt);

		e.preventDefault();
	};

	/**
	 * Submit issue form
	 * @param {object} param
	 */
	const submitIssue = (param) => {
		const notify = props.notify;
		const invoiceNoToSend = invoiceNo;
		const paymentDate = param.paymentDate;
		const issuedAt = param.issuedAt;
		const issuedBy = param.issuedBy;
		const issuedByID = param.issuedByID;

		const patchParam = {
			paymentDate,
			issuedAt,
			issuedBy,
			issuedByID,
		};

		GYAxios.patch(`/receipts/${invoiceNoToSend}`, patchParam).then((res) => {
			const response = res.data;

			// Error notification
			if (response.error != null)
				notify({ type: "e", msg: "Receipt issue failed!" });
			// No rows affected notification
			else if (response.changedRows <= 0)
				notify({ type: "w", msg: "No changes to receipt" });
			// Update successful notification
			else notify({ type: "s", msg: "Receipt issued successfully" });

			resetForm();
		});
	};

	useEffect(() => {
		setSelectedReceipt(props.selectedReceipt);
		setSelectedDate(
			getInitStartDate(props.selectedReceipt?.receipt?.paymentDate),
		);

		const invoiceObj =
			props.selectedReceipt != null ? props.selectedReceipt.invoice : null;
		const receiptObj =
			props.selectedReceipt != null ? props.selectedReceipt.receipt : null;

		if (invoiceObj != null) {
			const refInv = Array.isArray(invoiceObj) ? invoiceObj[0] : invoiceObj;

			const tempMinDate = moment(refInv.dateData)
				.add(1, "month")
				.startOf("month");

			setMinDate(tempMinDate);
		}

		let lastUpdateObj = null;

		if (receiptObj.issuedBy != null) {
			lastUpdateObj = {
				issuedBy: receiptObj.issuedBy,
				issuedAt: moment(receiptObj.issuedAt).format("DD/MM/YYYY HH:mm:ss"),
			};
		}

		setLastUpdate(lastUpdateObj);
	}, [props.selectedReceipt]);

	useEffect(() => {
		const invoiceObj = selectedReceipt.invoice;

		if (invoiceObj == null) return;

		const receiptObj = {
			...selectedReceipt.receipt,
			paymentDate: selectedDate.format("YYYY-MM-DD"),
			issuedByID: userID,
			issuedBy: username,
		};

		const tempReceipts = { ...selectedReceipt, receipt: receiptObj };
		setSelectedReceipt(tempReceipts);
	}, [selectedDate]);

	return (
		<>
			<Col md="4" id="receipt-form-container">
				{/* Header */}
				<Row id="receipt-form-header">
					<h4>Issue receipt</h4>
				</Row>

				{/* Form section */}
				<Row id="receipt-form-content" onSubmit={handleSubmit}>
					<Form>
						{/* Form inputs */}
						<Form.Group as={Row} controlId="paymentDateInput">
							<Form.Label column md={4}>
								Payment Date:
							</Form.Label>
							<Col md={8} id="receipt-date-form">
								<DatePicker
									id="receipt-date"
									selected={localTimeToPickerTime(selectedDate.toDate())}
									onChange={(date) => {
										const dateObj = moment(pickerTimeTolocalTime(date));
										if (dateObj.isValid()) setSelectedDate(dateObj);
									}}
									dateFormat="dd/MM/yyyy"
									minDate={localTimeToPickerTime(minDate.toDate())}
									maxDate={localTimeToPickerTime(moment().toDate())}
								/>
							</Col>
						</Form.Group>

						{/* Show last updated info */}
						{lastUpdate != null && (
							<Form.Group as={Row} style={lastUpdateStyle}>
								Last updated by: <b>{lastUpdate.issuedBy}</b> at{" "}
								<i>{lastUpdate.issuedAt}</i>
							</Form.Group>
						)}

						{/* Form buttons */}
						<Form.Group as={Row} controlId="btn">
							<Col md="6">
								<Button
									className="receipt-form-btn"
									variant="secondary"
									onClick={resetForm}
								>
									Cancel
								</Button>
							</Col>
							<Col md="6">
								<Button
									variant="primary"
									as="input"
									type="submit"
									value="Submit"
									className="receipt-form-btn"
								/>
							</Col>
						</Form.Group>
					</Form>
				</Row>
			</Col>
			<Col md="8">
				{isMSAReceipt ? (
					<ReceiptTemplate_MSA
						invoiceNo={invoiceNo}
						forcePaymentDate={selectedDate.format("YYYY-MM-DD")}
					/>
				) : (
					<ReceiptTemplate
						invoiceNo={invoiceNo}
						forcePaymentDate={selectedDate.format("YYYY-MM-DD")}
					/>
				)}
			</Col>
		</>
	);
};

export default ReceiptForm;
